import { IntlShape, useIntl } from "react-intl";

import { FilterConfig } from "types/Common";

const ADDITIONAL_COLUMNS = [
  "is_dangerous_goods",
  "incoterms",
  "pickup_lead_time_in_days",
  "delivery_lead_time_in_days",
  "metric_shipping_factor",
  "lcl_charge_type",
  "tonne_per_cbm",
  "service_requirement",
];

const INCOTERM_OPTIONS = (intl: IntlShape) => [
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.exWorks",
      defaultMessage: "EXW - Ex Works",
    }),
    value: "exw",
  },
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.freeCarrier",
      defaultMessage: "FCA - Free Carrier",
    }),
    value: "fca",
  },
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.carriagePaidTo",
      defaultMessage: "CPT - Carriage Paid To",
    }),
    value: "cpt",
  },
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.carriageInsurancePaidTo",
      defaultMessage: "CIP - Carriage Insurance Paid To",
    }),
    value: "cip",
  },
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.deliveryAtTerminal",
      defaultMessage: "DAT - Delivery At Terminal",
    }),
    value: "dat",
  },
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.deliveryAtPlace",
      defaultMessage: "DAP - Delivery at Place",
    }),
    value: "dap",
  },
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.deliveryDutyPaid",
      defaultMessage: "DDP - Delivery Duty Paid",
    }),
    value: "ddp",
  },
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.freeAlongsideShip",
      defaultMessage: "FAS - Free Alongside Ship",
    }),
    value: "fas",
  },
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.freeOnBoard",
      defaultMessage: "FOB - Free On Board",
    }),
    value: "fob",
  },
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.costAndFreight",
      defaultMessage: "CFR - Cost and Freight",
    }),
    value: "cfr",
  },
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.costInsuranceFreight",
      defaultMessage: "CIF - Cost, Insurance and Freight",
    }),
    value: "cif",
  },
  {
    label: intl.formatMessage({
      id: "settings.payment-locations.deliveredAtPlaceUnloaded",
      defaultMessage: "DPU - Delivered at Place Unloaded",
    }),
    value: "dpu",
  },
];

const SERVICE_REQUIREMENT_OPTIONS = (intl: IntlShape) => [
  {
    label: intl.formatMessage({
      id: "service.doorToDoor",
      defaultMessage: "Door to door",
    }),
    value: "dtd",
  },
  {
    label: intl.formatMessage({
      id: "service.doorToPort",
      defaultMessage: "Door to port",
    }),
    value: "dtp",
  },
  {
    label: intl.formatMessage({
      id: "service.portToDoor",
      defaultMessage: "Port to Door",
    }),
    value: "ptd",
  },
  {
    label: intl.formatMessage({
      id: "service.portToPort",
      defaultMessage: "Port to Port",
    }),
    value: "ptp",
  },
];

export default function useRateCardFilters(): {
  additionalColumns: Array<string>;
  filterConfig: Array<FilterConfig>;
  getHiddenColumns: () => string[];
} {
  const intl = useIntl();

  const getHiddenColumns = () => {
    const hiddenColumns = localStorage.getItem("rate_card_hidden_columns");
    if (hiddenColumns) {
      return JSON.parse(hiddenColumns);
    }
    localStorage.setItem("rate_card_hidden_columns", JSON.stringify(ADDITIONAL_COLUMNS));
    return ADDITIONAL_COLUMNS;
  };

  const filterConfig: FilterConfig[] = [
    {
      filterType: "text",
      label: intl.formatMessage({ id: "general.provider", defaultMessage: "Provider" }),
      key: "provider",
    },
    {
      filterType: "freight_mode",
      label: intl.formatMessage({ id: "general.freightMode", defaultMessage: "Freight mode" }),
      key: "freight_mode",
    },
    {
      filterType: "date",
      label: intl.formatMessage({ id: "settings.rate-cards.startDate", defaultMessage: "Start Date" }),
      key: "start_date",
    },
    {
      filterType: "date",
      label: intl.formatMessage({ id: "settings.rate-cards.expiryDate", defaultMessage: "Expiry Date" }),
      key: "expiry_date",
    },
    {
      filterType: "text",
      label: intl.formatMessage({ id: "settings.rate-cards.laneOrigin", defaultMessage: "Lane - Origin" }),
      key: "lane_origin",
    },
    {
      filterType: "text",
      label: intl.formatMessage({
        id: "settings.rate-cards.laneDestination",
        defaultMessage: "Lane - Destination",
      }),
      key: "lane_destination",
    },
    {
      filterType: "multiselect",
      label: intl.formatMessage({
        id: "general.status",
        defaultMessage: "Status",
      }),
      key: "is_active",
      options: [
        {
          label: intl.formatMessage({ id: "general.active", defaultMessage: "Active" }),
          value: true,
        },
        {
          label: intl.formatMessage({ id: "general.inActive", defaultMessage: "Inactive" }),
          value: false,
        },
      ],
    },
    {
      filterType: "multiselect",
      label: intl.formatMessage({
        id: "settings.rate-cards.dangerousGoods",
        defaultMessage: "Dangerous goods",
      }),
      key: "dangerous_goods",
      options: [
        {
          label: intl.formatMessage({ id: "general.yes", defaultMessage: "Yes" }),
          value: true,
        },
        {
          label: intl.formatMessage({ id: "general.no", defaultMessage: "No" }),
          value: false,
        },
      ],
    },
    {
      filterType: "multiselect",
      label: intl.formatMessage({
        id: "settings.rate-cards.incoterms",
        defaultMessage: "Incoterms",
      }),
      key: "incoterms",
      isSearchable: true,
      options: INCOTERM_OPTIONS(intl),
    },
    {
      filterType: "multiselect",
      label: intl.formatMessage({
        id: "new-request.service.serviceRequirement",
        defaultMessage: "Service requirement",
      }),
      key: "service_requirement",
      isSearchable: true,
      options: SERVICE_REQUIREMENT_OPTIONS(intl),
    },
  ];

  return { additionalColumns: ADDITIONAL_COLUMNS, filterConfig, getHiddenColumns };
}
